/**
 * Auth business logic operations - separated from React state
 */

import { supabase } from '@/integrations/supabase/client';
import { parseSupabaseError } from '@/utils/errorHandling';
import { secureLogout } from '@/utils/auth-logout';
import type { AuthResult } from './types';

/**
 * Server-side validation of email/phone availability.
 * Runs through the `validate-signup` edge function so that the underlying
 * existence-check functions are never exposed to the browser (prevents
 * user enumeration).
 */
const validateSignupData = async (payload: { email: string; phone?: string }) => {
  const { data, error } = await supabase.functions.invoke('validate-signup', {
    body: payload,
  });

  if (error) {
    console.error('[Validation] Erro ao validar dados de cadastro');
    return null;
  }

  return data as { valid: boolean; errors?: Record<string, string> } | null;
};

/**
 * Check if an email already exists in the system
 */
export const checkEmailExistsOperation = async (email: string): Promise<boolean> => {
  const result = await validateSignupData({ email: email.trim().toLowerCase() });
  return Boolean(result && result.valid === false && result.errors?.email);
};

/**
 * Check if a phone number already exists in the system
 */
export const checkPhoneExistsOperation = async (phone: string): Promise<boolean> => {
  const normalizedPhone = phone.replace(/\D/g, '');
  if (!normalizedPhone) return false;

  // Email is required by the validation endpoint; use a placeholder that is
  // never persisted so only the phone check result is evaluated.
  const result = await validateSignupData({
    email: `check-${normalizedPhone}@validation.local`,
    phone: normalizedPhone,
  });

  return Boolean(result && result.valid === false && result.errors?.phone);
};


/**
 * Sign up a new user
 */
export const signUpOperation = async (
  email: string, 
  password: string, 
  displayName?: string, 
  phone?: string
): Promise<{ data: any; error: any }> => {
  console.log('[AuthContext] Starting signup:', { email, displayName, hasPhone: !!phone });

  const redirectUrl = `${window.location.origin}/dashboard`;
  
  const { data, error } = await supabase.auth.signUp({
    email: email.trim().toLowerCase(),
    password,
    options: {
      emailRedirectTo: redirectUrl,
      data: {
        display_name: displayName || email.split('@')[0],
        phone: phone || null,
      }
    }
  });

  return { data, error };
};

/**
 * Sign in with email and password
 */
export const signInOperation = async (
  email: string, 
  password: string
): Promise<AuthResult> => {
  const { error } = await supabase.auth.signInWithPassword({
    email,
    password
  });

  return { error };
};

/**
 * Sign in with Google OAuth
 */
export const signInWithGoogleOperation = async (): Promise<AuthResult> => {
  const redirectUrl = `${window.location.origin}/dashboard`;
  
  const { error } = await supabase.auth.signInWithOAuth({
    provider: 'google',
    options: {
      redirectTo: redirectUrl,
      queryParams: {
        access_type: 'offline',
        prompt: 'consent',
      },
    },
  });

  return { error };
};

/**
 * Sign out the current user
 */
export const signOutOperation = async (): Promise<void> => {
  await secureLogout();
};

/**
 * Send password reset email
 */
export const resetPasswordOperation = async (email: string): Promise<AuthResult> => {
  const { error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: `${window.location.origin}/reset-password`
  });

  return { error };
};

/**
 * Update user password
 */
export const updatePasswordOperation = async (newPassword: string): Promise<AuthResult> => {
  const { error } = await supabase.auth.updateUser({
    password: newPassword
  });

  return { error };
};

/**
 * Get current session
 */
export const getSessionOperation = async () => {
  return supabase.auth.getSession();
};

/**
 * Subscribe to auth state changes
 */
export const onAuthStateChange = (
  callback: (event: string, session: any) => void
) => {
  return supabase.auth.onAuthStateChange(callback);
};

/**
 * Ensure user profile exists (backup for trigger)
 */
export const ensureProfileExists = async (user: { 
  id: string; 
  email?: string; 
  user_metadata?: { display_name?: string; phone?: string } 
}): Promise<void> => {
  try {
    const { data: profileCheck } = await supabase
      .from('profiles')
      .select('id')
      .eq('id', user.id)
      .maybeSingle();

    if (!profileCheck) {
      await supabase.from('profiles').insert({
        id: user.id,
        display_name: user.user_metadata?.display_name || user.email?.split('@')[0],
        email: user.email,
        phone: user.user_metadata?.phone || null
      });
    }
  } catch (err) {
    console.warn('Profile será criado automaticamente:', err);
  }
};

/**
 * Ensure user has at least one business profile (personal profile on signup)
 */
export const ensureBusinessProfileExists = async (user: { 
  id: string; 
  email?: string; 
  user_metadata?: { display_name?: string } 
}): Promise<void> => {
  try {
    // Check if user already has ANY business profile
    const { data: existingProfiles, error: checkError } = await supabase
      .from('business_profiles')
      .select('id')
      .eq('user_id', user.id)
      .limit(1);

    // If user has any profile, don't create another
    if (existingProfiles && existingProfiles.length > 0) {
      console.log('[Auth] User already has a business profile, skipping creation');
      return;
    }

    if (checkError) {
      console.error('[Auth] Error checking business profile:', checkError);
      return;
    }

    // Extract first name from display_name or email
    const displayName = user.user_metadata?.display_name || user.email?.split('@')[0] || 'Usuário';
    const firstName = displayName.split(' ')[0];
    
    const { error } = await supabase.from('business_profiles').insert({
      user_id: user.id,
      name: firstName,
      type: 'personal',
      color: '#3b82f6',
      is_active: true,
      timezone: 'America/Sao_Paulo'
    });

    // Ignore unique violation errors (profile already exists from trigger)
    if (error) {
      const isDuplicateError = error.message?.includes('duplicate') || 
                               error.code === '23505' ||
                               error.message?.includes('unique');
      
      if (!isDuplicateError) {
        console.error('[Auth] Error creating business profile:', error);
      } else {
        console.log('[Auth] Profile already exists (created by trigger), skipping');
      }
    } else {
      console.log('[Auth] Personal profile created automatically for user:', user.id);
    }
  } catch (err) {
    console.warn('[Auth] Business profile creation warning:', err);
  }
};

/**
 * Parse and format Supabase auth errors
 */
export const parseAuthError = parseSupabaseError;

/**
 * Handle remember me functionality
 */
export const handleRememberMe = (rememberMe: boolean): void => {
  if (!rememberMe) {
    sessionStorage.setItem('clear_session_on_close', 'true');
    console.log('[Auth] Session will be cleared when browser closes');
  } else {
    sessionStorage.removeItem('clear_session_on_close');
  }
};

/**
 * Check if session should be cleared on close
 */
export const shouldClearSessionOnClose = (): boolean => {
  return sessionStorage.getItem('clear_session_on_close') === 'true';
};

/**
 * Clear the session on close flag
 */
export const clearSessionOnCloseFlag = (): void => {
  sessionStorage.removeItem('clear_session_on_close');
};
